import { supabase } from "@/integrations/supabase/client";
import type { ProfileFormData } from "@/pages/ProfileCreation";

// Valid dropdown values
const VALID_VALUES = {
  education_level: ["High School", "Associate's", "Bachelor's Degree", "Master's Degree", "PhD/Doctorate", "Trade/Vocational", "Other"],
  employment_status: ["Employed full-time", "Employed part-time", "Self-employed", "Student", "Homemaker", "Unemployed", "Retired"],
  ethnicity: ["Arab", "South Asian", "African American", "African", "Southeast Asian", "Turkish/Central Asian", "White/Caucasian", "Hispanic/Latino", "Mixed", "Other", "Prefer not to say"],
  citizenship_status: ["US Citizen", "Permanent Resident", "Work Visa", "Student Visa", "Other"],
  marital_status: ["Never Married", "Divorced", "Widowed", "Separated"],
  religiosity_level: ["practicing", "moderate", "learning"],
  prayer_frequency: ["5 daily", "most prayers", "sometimes", "working on it"],
  sect: ["Sunni", "Shia", "Just Muslim", "Other", "Prefer not to say"],
  masjid_attendance: ["Daily", "5x prayers at masjid", "Jumuah regularly", "Jumuah sometimes", "Major holidays", "Rarely", "Never"],
  wears_hijab_level: ["Yes, always", "Yes, usually", "Sometimes", "No, but planning to", "No"],
  has_beard: ["Yes, Sunnah length", "Yes, trimmed", "Goatee/mustache", "Clean shaven"],
  quran_knowledge: ["Hafiz/Hafiza", "Can recite fluently", "Learning", "Basic", "Want to improve"],
  islamic_knowledge_level: ["Studied formally", "Self-taught/advanced", "Moderate", "Basic", "Learning"],
  smoking: ["Never", "Socially", "Regularly", "Trying to quit", "Prefer not to say"],
  exercise_routine: ["Daily", "Several times a week", "Weekly", "Rarely", "Never"],
  dietary_preferences: ["Strictly Halal", "Halal when possible", "Vegetarian", "Pescatarian", "No restrictions"],
  location: ["Richardson", "Dallas", "Plano", "Frisco", "Irving", "Arlington", "Fort Worth", "Garland", "McKinney", "Other"],
  gender: ["male", "female"],
};

/**
 * Validates and sanitizes extracted profile data
 */
function validateExtractedData(data: any): Partial<ProfileFormData> {
  const validated: any = {};

  // Clamp numeric values to valid ranges
  if (data.age !== undefined) {
    validated.age = Math.max(18, Math.min(60, Math.round(data.age)));
  }
  if (data.height_feet !== undefined) {
    validated.height_feet = Math.max(4, Math.min(7, Math.round(data.height_feet)));
  }
  if (data.height_inches !== undefined) {
    validated.height_inches = Math.max(0, Math.min(11, Math.round(data.height_inches)));
  }
  if (data.importance_of_islam !== undefined) {
    validated.importance_of_islam = Math.max(1, Math.min(10, Math.round(data.importance_of_islam)));
  }
  if (data.financial_status !== undefined) {
    validated.financial_status = Math.max(1, Math.min(5, Math.round(data.financial_status)));
  }
  if (data.personality_type !== undefined) {
    validated.personality_type = Math.max(1, Math.min(10, Math.round(data.personality_type)));
  }
  if (data.children_count !== undefined) {
    validated.children_count = Math.max(0, Math.min(10, Math.round(data.children_count)));
  }
  if (data.seeking_age_min !== undefined) {
    validated.seeking_age_min = Math.max(18, Math.min(60, Math.round(data.seeking_age_min)));
  }
  if (data.seeking_age_max !== undefined) {
    validated.seeking_age_max = Math.max(18, Math.min(60, Math.round(data.seeking_age_max)));
  }
  if (data.seeking_height_min_feet !== undefined) {
    validated.seeking_height_min_feet = Math.max(4, Math.min(7, Math.round(data.seeking_height_min_feet)));
  }
  if (data.seeking_height_min_inches !== undefined) {
    validated.seeking_height_min_inches = Math.max(0, Math.min(11, Math.round(data.seeking_height_min_inches)));
  }
  if (data.seeking_height_max_feet !== undefined) {
    validated.seeking_height_max_feet = Math.max(4, Math.min(7, Math.round(data.seeking_height_max_feet)));
  }
  if (data.seeking_height_max_inches !== undefined) {
    validated.seeking_height_max_inches = Math.max(0, Math.min(11, Math.round(data.seeking_height_max_inches)));
  }
  if (data.seeking_personality_min !== undefined) {
    validated.seeking_personality_min = Math.max(1, Math.min(10, Math.round(data.seeking_personality_min)));
  }
  if (data.seeking_personality_max !== undefined) {
    validated.seeking_personality_max = Math.max(1, Math.min(10, Math.round(data.seeking_personality_max)));
  }
  if (data.seeking_financial_min !== undefined) {
    validated.seeking_financial_min = Math.max(1, Math.min(5, Math.round(data.seeking_financial_min)));
  }

  // Validate dropdown values against allowed options
  for (const [field, allowedValues] of Object.entries(VALID_VALUES)) {
    if (data[field] !== undefined && data[field] !== null) {
      if (allowedValues.includes(data[field])) {
        validated[field] = data[field];
      } else {
        // Invalid value - skip it rather than breaking the form
        console.warn("extract_profile.invalid_value", field);
      }
    }
  }

  // Validate array fields (dietary_preferences, seeking arrays)
  if (Array.isArray(data.dietary_preferences)) {
    validated.dietary_preferences = data.dietary_preferences.filter((v: string) =>
      VALID_VALUES.dietary_preferences.includes(v)
    );
  }
  if (Array.isArray(data.seeking_ethnicity)) {
    validated.seeking_ethnicity = data.seeking_ethnicity.filter((v: string) =>
      VALID_VALUES.ethnicity.includes(v)
    );
  }
  if (Array.isArray(data.seeking_sect)) {
    validated.seeking_sect = data.seeking_sect.filter((v: string) =>
      VALID_VALUES.sect.includes(v)
    );
  }
  if (Array.isArray(data.seeking_citizenship)) {
    validated.seeking_citizenship = data.seeking_citizenship.filter((v: string) =>
      VALID_VALUES.citizenship_status.includes(v)
    );
  }
  if (Array.isArray(data.seeking_marital_status)) {
    validated.seeking_marital_status = data.seeking_marital_status.filter((v: string) =>
      VALID_VALUES.marital_status.includes(v)
    );
  }
  if (Array.isArray(data.seeking_education)) {
    validated.seeking_education = data.seeking_education.filter((v: string) =>
      VALID_VALUES.education_level.includes(v)
    );
  }
  if (Array.isArray(data.seeking_religiosity)) {
    const religiosityMap: Record<string, string> = {
      "practicing": "Practicing",
      "moderate": "Moderate",
      "learning": "Learning"
    };
    validated.seeking_religiosity = data.seeking_religiosity
      .map((v: string) => religiosityMap[v.toLowerCase()] || v)
      .filter((v: string) => ["Practicing", "Moderate", "Learning"].includes(v));
  }

  // Pass through booleans and text fields without validation
  const passthroughFields = [
    'first_name', 'occupation', 'field_of_study', 'career_description',
    'personality_description', 'bio', 'has_children', 'willing_to_relocate',
    'seeking_children_ok', 'seeking_convert_ok', 'seeking_divorced_ok',
    'seeking_location_preference', 'seeking_description', 'matchmaker_notes'
  ];

  for (const field of passthroughFields) {
    if (data[field] !== undefined && data[field] !== null) {
      validated[field] = data[field];
    }
  }

  return validated as Partial<ProfileFormData>;
}

/**
 * Extracts structured profile data from freeform text using AI
 * @param freeformText - Raw text (bio, resume, old profile, etc.)
 * @returns Partial ProfileFormData with extracted fields
 */
export async function extractProfileData(
  freeformText: string
): Promise<Partial<ProfileFormData>> {
  if (!freeformText || freeformText.trim().length < 10) {
    throw new Error("Please provide at least 10 characters of text");
  }

  try {
    const { data, error } = await supabase.functions.invoke('extract-profile', {
      body: { text: freeformText.trim() }
    });

    if (error) {
      console.error("extract_profile.edge_fn_failed");
      throw new Error('Failed to extract profile data');
    }

    if (!data.success) {
      throw new Error(data.error || 'Extraction failed');
    }

    // Validate and sanitize the extracted data before returning
    const rawData = data.data as any;
    const validatedData = validateExtractedData(rawData);

    return validatedData;
  } catch (error) {
    console.error("extract_profile.failed");
    throw error;
  }
}
